// File: src/lib/graphql/queries/sprint.ts
// Purpose: Sprint Management GraphQL Queries and Mutations
// InstaDev - Sprint API

import { gql } from '@apollo/client'

// ============================================
// TYPES
// ============================================

export enum SprintStatus {
  PLANNING = 'PLANNING',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum BoardType {
  KANBAN = 'KANBAN',
  SCRUM = 'SCRUM'
}

export interface BurndownData {
  date: string
  remainingPoints: number
  idealPoints: number
}

export interface Retrospective {
  whatWentWell: string[]
  whatCouldImprove: string[]
  actionItems: string[]
}

export interface Employee {
  _id: string
  azureId?: string
  azureTenantId?: string
  azureEmail?: string
  azureDisplayName?: string
  ssoEnabled?: boolean
  lastAzureLogin?: string
  azureGroups?: string[]
  firstName: string
  lastName: string
  email: string
  countryCode?: string
  mobile_number?: string
  languages?: string[]
  joining_date?: string
  skills?: string[]
  designation?: string
  department?: string
  shift?: string
  hourlyRate?: number
  overtimeRate?: number
  resume?: string
  is_active: boolean
  is_deleted?: boolean
  employee_left?: boolean
  leaving_date?: string
  createdAt: string
  updatedAt: string
}

export interface Board {
  id: string
  name: string
  type: BoardType
  filterQuery?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface Sprint {
  id: string
  _id?: string // For backward compatibility
  name: string
  goal?: string
  status: SprintStatus
  projectId?: string
  project?: SimpleProject // Project object when populated
  startDate: string
  endDate: string
  actualEndDate?: string
  velocity?: number
  taskCount?: number
  completedTaskCount?: number
  remainingDays?: number
  burndownData?: BurndownData[]
  initialCommitment?: number
  completedPoints?: number
  addedDuringSprint?: number
  removedDuringSprint?: number
  retrospective?: Retrospective
  board?: Board
  createdBy?: Employee
  completedBy?: Employee
  is_active?: boolean
  is_deleted?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface IssueType {
  id: string
  name: string
  description?: string
  icon?: string
  color?: string
  hierarchyLevel?: number
  allowsSubtasks?: boolean
  requiredFields?: string[]
  optionalFields?: string[]
  isSystem?: boolean
  isActive?: boolean
  isEpic?: boolean
  isSubtask?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface Comment {
  id: string
  content: string
  isEdited?: boolean
  createdAt: string
  updatedAt: string
}

export interface ActivityLog {
  id: string
  action: string
  field?: string
  oldValue?: string
  newValue?: string
  timestamp: string
}

export interface Attachment {
  id: string
  name: string
  url: string
  type: string
  size: number
  uploadedAt: string
}

export interface LinkedTask {
  linkType: string
  task?: TaskReference
}

export interface TaskReference {
  id: string
  task: string
  status?: string
  date?: string
  startTime?: string
  endTime?: string
  totalHours?: number
  description?: string
  note?: string
  priority?: string
  category?: string
  tags?: string[]
  issueKey?: string
  storyPoints?: number
  originalEstimate?: number
  remainingEstimate?: number
  timeSpent?: number
  boardPosition?: number
  commentCount?: number
  resolution?: string
  dueDate?: string
  completedDate?: string
  isOverdue?: boolean
  isBlocked?: boolean
  reassignedAt?: string
  reassignmentReason?: string
  isReassigned?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface ReassignmentHistory {
  reassignedAt: string
  reason?: string
  status?: string
  completedAt?: string
  lastStatusUpdatedAt?: string
  reassignedBy?: Employee
  previousEmployee?: Employee
  newEmployee?: Employee
}

export interface User {
  _id: string
  username: string
  email: string
  isAdmin?: boolean
  isSuperAdmin?: boolean
  isActive?: boolean
}

export interface SimpleSprint {
  id: string
  name: string
  goal?: string
  status: SprintStatus
  startDate: string
  endDate: string
  actualEndDate?: string
  initialCommitment?: number
  completedPoints?: number
  addedDuringSprint?: number
  removedDuringSprint?: number
  velocity?: number
  remainingDays?: number
  taskCount?: number
  completedTaskCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface Task {
  id: string
  _id?: string
  task: string
  status?: string
  date?: string
  startTime?: string
  endTime?: string
  totalHours?: number
  description?: string
  note?: string
  priority?: string
  category?: string
  tags?: string[]
  issueKey?: string
  storyPoints?: number
  originalEstimate?: number
  remainingEstimate?: number
  timeSpent?: number
  boardPosition?: number
  commentCount?: number
  resolution?: string
  dueDate?: string
  completedDate?: string
  isOverdue?: boolean
  isBlocked?: boolean
  reassignedAt?: string
  reassignmentReason?: string
  isReassigned?: boolean
  createdAt?: string
  updatedAt?: string
  employee?: Employee
  assignedBy?: Employee
  issueType?: IssueType
  parentTask?: TaskReference
  subtasks?: TaskReference[]
  epicLink?: TaskReference
  sprint?: SimpleSprint
  comments?: Comment[]
  activityLog?: ActivityLog[]
  watchers?: Employee[]
  linkedTasks?: LinkedTask[]
  attachments?: Attachment[]
  blockedBy?: TaskReference[]
  reassignedBy?: User
  previousEmployee?: Employee
  reassignmentHistory?: ReassignmentHistory[]
  sprintId?: string
  assignedTo?: Employee
}

export interface SprintVelocity {
  velocity: number
  commitment: number
  completed: number
  sprint: {
    id: string
    name: string
    goal?: string
    status: SprintStatus
    startDate: string
    endDate: string
    actualEndDate?: string
    initialCommitment?: number
    completedPoints?: number
    addedDuringSprint?: number
    removedDuringSprint?: number
    velocity?: number
    remainingDays?: number
    taskCount?: number
    completedTaskCount?: number
    createdAt: string
    updatedAt: string
    board?: Board
  }
}

export interface CreateSprintInput {
  name: string
  goal?: string
  projectId: string
  boardId?: string
  startDate: string
  endDate: string
}

export interface StartSprintInput {
  sprintId: string
  startDate?: string
}

export interface CompleteSprintInput {
  sprintId: string
  moveIncompleteToSprintId?: string | null
  statusesToMove?: string[] | null
  retrospective?: {
    whatWentWell: string[]
    whatCouldImprove: string[]
    actionItems: string[]
  } | null
}

export interface UpdateSprintInput {
  name?: string
  goal?: string
  startDate?: string
  endDate?: string
}

export interface SimpleProject {
  _id: string
  name: string
  code: string
  status: string
  is_active: boolean
}

// ============================================
// QUERIES
// ============================================

export const GET_SPRINT = gql`
  query GetSprint($id: ID!) {
    sprint(id: $id) {
      id
      name
      goal
      status
      startDate
      endDate
      actualEndDate
      initialCommitment
      completedPoints
      addedDuringSprint
      removedDuringSprint
      velocity
      remainingDays
      taskCount
      completedTaskCount
      retrospective {
        whatWentWell
        whatCouldImprove
        actionItems
      }
      board {
        id
        name
        type
        filterQuery
        isActive
        createdAt
        updatedAt
      }
      createdBy {
        _id
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      completedBy {
        _id
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`

// Minimal sprint query for resolving sprint names from IDs
export const GET_SPRINT_BY_ID = gql`
  query GetSprintById($id: ID!) {
    sprint(id: $id) {
      id
      name
      createdAt
      updatedAt
    }
  }
`

export const GET_SPRINTS = gql`
  query GetSprints($projectIds: [ID!]!, $status: SprintStatus) {
    sprints(projectIds: $projectIds, status: $status) {
      id
      name
      goal
      status
      startDate
      endDate
      actualEndDate
      initialCommitment
      completedPoints
      addedDuringSprint
      removedDuringSprint
      velocity
      remainingDays
      taskCount
      completedTaskCount
      createdAt
      updatedAt
      project {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      completedBy {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
    }
  }
`

export const GET_ACTIVE_SPRINT = gql`
  query GetActiveSprint($projectIds: [ID!]!) {
    activeSprint(projectIds: $projectIds) {
      id
      name
      goal
      status
      startDate
      endDate
      actualEndDate
      remainingDays
      velocity
      taskCount
      completedTaskCount
      initialCommitment
      completedPoints
      addedDuringSprint
      removedDuringSprint
      burndownData {
        date
        remainingPoints
        idealPoints
      }
      board {
        id
        name
        type
        filterQuery
        isActive
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`

export const GET_BACKLOG = gql`
  query GetBacklog($projectIds: [ID!]!) {
    backlog(projectIds: $projectIds) {
      id
      status
      date
      startTime
      endTime
      totalHours
      description
      note
      priority
      category
      tags
      issueKey
      storyPoints
      originalEstimate
      remainingEstimate
      timeSpent
      boardPosition
      commentCount
      resolution
      dueDate
      completedDate
      isOverdue
      isBlocked
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
      task
      employee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      assignedBy {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      issueType {
        id
        name
        description
        icon
        color
        hierarchyLevel
        allowsSubtasks
        requiredFields
        optionalFields
        isSystem
        isActive
        isEpic
        isSubtask
        createdAt
        updatedAt
      }
      parentTask {
        id
        task
        status
        date
        startTime
        endTime
        totalHours
        description
        note
        priority
        category
        tags
        issueKey
        storyPoints
        originalEstimate
        remainingEstimate
        timeSpent
        boardPosition
        commentCount
        resolution
        dueDate
        completedDate
        isOverdue
        isBlocked
        reassignedAt
        reassignmentReason
        isReassigned
        createdAt
        updatedAt
      }
      subtasks {
        id
        task
        status
        date
        startTime
        endTime
        totalHours
        description
        note
        priority
        category
        tags
        issueKey
        storyPoints
        originalEstimate
        remainingEstimate
        timeSpent
        boardPosition
        commentCount
        resolution
        dueDate
        completedDate
        isOverdue
        isBlocked
        reassignedAt
        reassignmentReason
        isReassigned
        createdAt
        updatedAt
      }
      epicLink {
        id
        task
        status
        date
        startTime
        endTime
        totalHours
        description
        note
        priority
        category
        tags
        issueKey
        storyPoints
        originalEstimate
        remainingEstimate
        timeSpent
        boardPosition
        commentCount
        resolution
        dueDate
        completedDate
        isOverdue
        isBlocked
        reassignedAt
        reassignmentReason
        isReassigned
        createdAt
        updatedAt
      }
      sprint {
        id
        name
        goal
        status
        startDate
        endDate
        actualEndDate
        initialCommitment
        completedPoints
        addedDuringSprint
        removedDuringSprint
        velocity
        remainingDays
        taskCount
        completedTaskCount
        createdAt
        updatedAt
      }
      comments {
        id
        content
        isEdited
        createdAt
        updatedAt
      }
      activityLog {
        id
        action
        field
        oldValue
        newValue
        timestamp
      }
      watchers {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      linkedTasks {
        linkType
      }
      attachments {
        id
        name
        url
        type
        size
        uploadedAt
      }
      blockedBy {
        id
        task
        status
        date
        startTime
        endTime
        totalHours
        description
        note
        priority
        category
        tags
        issueKey
        storyPoints
        originalEstimate
        remainingEstimate
        timeSpent
        boardPosition
        commentCount
        resolution
        dueDate
        completedDate
        isOverdue
        isBlocked
        reassignedAt
        reassignmentReason
        isReassigned
        createdAt
        updatedAt
      }
      reassignedBy {
        _id
        username
        email
        isAdmin
        isSuperAdmin
        isActive
      }
      previousEmployee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      reassignmentHistory {
        reassignedAt
        reason
        status
        completedAt
        lastStatusUpdatedAt
      }
    }
  }
`

export const GET_SPRINT_VELOCITY = gql`
  query GetSprintVelocity($projectIds: [ID!]!, $sprintCount: Int) {
    sprintVelocity(projectIds: $projectIds, sprintCount: $sprintCount) {
      velocity
      commitment
      completed
      sprint {
        id
        name
        goal
        status
        startDate
        endDate
        actualEndDate
        initialCommitment
        completedPoints
        addedDuringSprint
        removedDuringSprint
        velocity
        remainingDays
        taskCount
        completedTaskCount
        createdAt
        updatedAt
        board {
          id
          name
          type
          filterQuery
          isActive
          createdAt
          updatedAt
        }
      }
    }
  }
`

export const GET_AVERAGE_VELOCITY = gql`
  query GetAverageVelocity($projectIds: [ID!]!, $sprintCount: Int) {
    averageVelocity(projectIds: $projectIds, sprintCount: $sprintCount)
  }
`

export const GET_ALL_PROJECTS = gql`
  query GetAllProjects($limit: Int) {
    projects(limit: $limit) {
      edges {
        _id
        name
        code
        status
        is_active
      }
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const CREATE_SPRINT = gql`
  mutation CreateSprint($input: CreateSprintInput!) {
    createSprint(input: $input) {
      id
      name
      goal
      status
      startDate
      endDate
      actualEndDate
      initialCommitment
      completedPoints
      addedDuringSprint
      removedDuringSprint
      velocity
      remainingDays
      taskCount
      completedTaskCount
      createdAt
      updatedAt
    }
  }
`

export const START_SPRINT = gql`
  mutation StartSprint($input: StartSprintInput!) {
    startSprint(input: $input) {
      id
      name
      goal
      status
      startDate
      endDate
      actualEndDate
      initialCommitment
      completedPoints
      addedDuringSprint
      removedDuringSprint
      velocity
      remainingDays
      taskCount
      completedTaskCount
      createdAt
      updatedAt
    }
  }
`

export const COMPLETE_SPRINT = gql`
  mutation CompleteSprint($input: CompleteSprintInput!) {
    completeSprint(input: $input) {
      id
      name
      status
      velocity
      completedPoints
      completedTaskCount
      taskCount
      actualEndDate
      retrospective {
        whatWentWell
        whatCouldImprove
        actionItems
      }
      updatedAt
    }
  }
`

export const ADD_TASK_TO_SPRINT = gql`
  mutation AddTaskToSprint($taskId: ID!, $sprintId: ID!) {
    addTaskToSprint(taskId: $taskId, sprintId: $sprintId) {
      id
      task
      sprint {
        id
        name
        status
      }
      storyPoints
      status
      updatedAt
    }
  }
`

export const REMOVE_TASK_FROM_SPRINT = gql`
  mutation RemoveTaskFromSprint($taskId: ID!) {
    removeTaskFromSprint(taskId: $taskId) {
      id
      task
      sprint {
        id
        name
      }
      storyPoints
      status
      updatedAt
    }
  }
`

export const MOVE_TASK_TO_SPRINT = gql`
  mutation MoveTaskToSprint($taskId: ID!, $toSprintId: ID!) {
    moveTaskToSprint(taskId: $taskId, toSprintId: $toSprintId) {
      id
      task
      sprint {
        id
        name
        status
      }
      storyPoints
      status
      updatedAt
    }
  }
`

export const DELETE_SPRINT = gql`
  mutation DeleteSprint($id: ID!) {
    deleteSprint(id: $id)
  }
`

export const UPDATE_SPRINT = gql`
  mutation UpdateSprint($id: ID!, $input: UpdateSprintInput!) {
    updateSprint(id: $id, input: $input) {
      id
      name
      goal
      status
      startDate
      endDate
      actualEndDate
      initialCommitment
      completedPoints
      addedDuringSprint
      removedDuringSprint
      velocity
      remainingDays
      taskCount
      completedTaskCount
      createdAt
      updatedAt
    }
  }
`

export const BULK_REORDER_SPRINT_TASKS = gql`
  mutation BulkReorderSprintTasks($sprintId: ID!, $taskIds: [ID!]!) {
    bulkReorderSprintTasks(sprintId: $sprintId, taskIds: $taskIds) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      note
      priority
      category
      tags
      issueKey
      storyPoints
      originalEstimate
      remainingEstimate
      timeSpent
      boardPosition
      commentCount
      resolution
      dueDate
      completedDate
      isOverdue
      isBlocked
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
    }
  }
`

// ============================================
// ORGANIZATION QUERIES
// ============================================

export interface SimpleOrganization {
  _id: string
  name: string
  code: string
  is_active: boolean
  is_deleted?: boolean
}

export const GET_ALL_ORGANIZATIONS = gql`
  query GetAllOrganizations($limit: Int) {
    organizations(limit: $limit) {
      edges {
        _id
        name
        code
        is_active
        is_deleted
      }
    }
  }
`
